import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import '../styles/Pricing.css';

function Pricing() {
  const [isAnnual, setIsAnnual] = useState(false);
  const navigate = useNavigate();

  const pricingPlans = [
    {
      name: 'Starter',
      description: 'Perfect for startups beginning their hiring journey',
      price: 29,
      priceAnnual: 290,
      period: '/month',
      screenings: '50 screenings/month included',
      overage: '$0.50 per additional screening',
      features: [
        'Up to 100 resume screenings/month',
        'AI Resume Parsing',
        'Basic Match Scoring',
        'Email Support',
        'Standard ATS Integration',
        'Hiring Analytics Dashboard',
      ],
      highlighted: false,
      cta: 'Start Free Trial',
    },
    {
      name: 'Professional',
      description: 'Ideal for growing teams with regular hiring needs',
      price: 79,
      priceAnnual: 790,
      period: '/month',
      screenings: '200 screenings/month included',
      overage: '$0.35 per additional screening',
      features: [
        'Up to 1,000 resume screenings/month',
        'All Starter features',
        'AI Recruiter Copilot',
        'Bulk Hiring Mode',
        'Bias-Aware Screening',
        'Interview Question Generator',
        'Priority Support',
        'Custom Scoring Rules',
        'Advanced Analytics & Reports',
      ],
      highlighted: true,
      badge: 'Most Popular',
      cta: 'Start Free Trial',
    },
    {
      name: 'Enterprise',
      description: 'Built for high-volume hiring at scale',
      price: null,
      priceAnnual: null,
      period: '',
      screenings: 'Unlimited screenings',
      overage: null,
      features: [
        'Unlimited resume screenings',
        'All Professional features',
        'Autonomous Screening Agent',
        'Candidate DNA Profiles',
        'Resume vs Reality Check',
        'Predictive Hiring Analytics',
        'White-label Options',
        'Dedicated Account Manager',
        'Custom Integrations',
        'SLA & Premium Support',
      ],
      highlighted: false,
      cta: 'Contact Sales',
    },
  ];

  const handleContactClick = (e) => {
    e.preventDefault();
    navigate('/contact');
  };

  const formatPrice = (plan) => {
    if (plan.price === null) {
      return 'Custom';
    }
    if (isAnnual) {
      const monthlyPrice = (plan.priceAnnual / 12).toFixed(2);
      return `$${monthlyPrice}`;
    }
    return `$${plan.price}`;
  };

  const getAnnualSavings = (plan) => {
    if (plan.price === null) return null;
    const annualMonthly = plan.priceAnnual / 12;
    const savings = Math.round(((plan.price - annualMonthly) / plan.price) * 100);
    return savings;
  };

  return (
    <section id="pricing" className="pricing section">
      <div className="container">
        {/* Section Header */}
        <div className="section-header text-center">
          <h2 className="section-title">Simple, Transparent Pricing</h2>
          <p className="section-subtitle">
            Choose the plan that fits your hiring needs. All plans include core AI screening
            features and can be customized to match your workflow.
          </p>
        </div>

        {/* Billing Toggle */}
        <div className="billing-toggle">
          <span className={`billing-option ${!isAnnual ? 'active' : ''}`}>Monthly</span>
          <button
            className="toggle-switch"
            onClick={() => setIsAnnual(!isAnnual)}
            aria-label="Toggle billing period"
          >
            <span className={`toggle-slider ${isAnnual ? 'annual' : ''}`}></span>
          </button>
          <span className={`billing-option ${isAnnual ? 'active' : ''}`}>
            Annual
            <span className="savings-badge">Save 17%</span>
          </span>
        </div>

        {/* Pricing Cards */}
        <div className="pricing-grid">
          {pricingPlans.map((plan, index) => (
            <div
              key={index}
              className={`pricing-card card ${plan.highlighted ? 'highlighted' : ''}`}
            >
              {plan.badge && <div className="pricing-badge">{plan.badge}</div>}

              <div className="pricing-header">
                <h3 className="plan-name">{plan.name}</h3>
                <p className="plan-description">{plan.description}</p>
              </div>

              <div className="pricing-price">
                <span className="price">{formatPrice(plan)}</span>
                {plan.period && <span className="period">{plan.period}</span>}
                {isAnnual && plan.price !== null && (
                  <div className="annual-details">
                    <span className="billed-text">Billed ${plan.priceAnnual}/year</span>
                  </div>
                )}
              </div>

              {plan.screenings && (
                <div className="screenings-info">
                  <p className="screenings-text">{plan.screenings}</p>
                  {plan.overage && (
                    <p className="overage-text">{plan.overage}</p>
                  )}
                </div>
              )}

              <ul className="pricing-features">
                {plan.features.map((feature, featureIndex) => (
                  <li key={featureIndex} className="feature-item">
                    <span className="feature-check">✓</span>
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>

              <div className="pricing-cta">
                {plan.name === 'Enterprise' ? (
                  <a
                    href="/contact"
                    className="btn btn-secondary btn-large"
                    onClick={handleContactClick}
                  >
                    {plan.cta}
                  </a>
                ) : (
                  <a
                    href="/contact"
                    className={`btn ${plan.highlighted ? 'btn-primary' : 'btn-secondary'} btn-large`}
                    onClick={handleContactClick}
                  >
                    {plan.cta}
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Additional Info */}
        <div className="pricing-info text-center">
          <p className="info-text">
            All plans include a 14-day free trial. No credit card required. Cancel anytime.
          </p>
          <p className="info-text">
            Need a custom plan?{' '}
            <a href="/contact" onClick={handleContactClick} className="text-primary">
              Contact our sales team
            </a>
            {' '}for enterprise pricing and volume discounts.
          </p>
        </div>
      </div>
    </section>
  );
}

export default Pricing;
